import React, { useEffect, useMemo, useRef, useState } from 'react';
import { App, Spin, Alert, Typography } from 'antd';
import { ArrowLeft, Calendar, ListChecks, Mic, RotateCw } from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  useSessionToken,
  useIsGuest,
  useSessionRoutes,
  usePageHeaderAction,
  useEnterAssessment,
  useRevokedKickOut,
} from '@/hooks';
import { motion } from 'framer-motion';
import { useInterviewStore } from '@/store/interviewStore';
import { useSessionWizardStore } from '@/store';
import { AssessmentService, AssessmentStatus, BaseAPIClient, InterviewTemplatesService } from '@/services/api';
import { AssessmentInputMode } from '@evaltick/shared';
import { formatDate, pluralize } from '@/utils';
import type { Assessment } from '@/services/api';
import { NextPartModal, wasPrompted, markPrompted } from '@/components/NextPartModal';
import { partsOf, remainingPart, remainingPartCopy, type TemplatePart } from '@/pages/Sessions/interviewStatus';
import { usePageBackLink } from '@/hooks';
import { EvalChart } from '@/components/Interview/EvalChart';
import { RetakeModal } from '@/components/Interview/RetakeModal';
import { Button as BrandButton } from '@/components/Button';
import styles from './SessionResults.module.scss';

const { Title } = Typography;

const SUMMARY_POLL_INTERVAL_MS = 2_000;
// ~60s — must outlast the API's 30s retry wait (SUMMARY_GENERATION_TIMEOUT_MS),
// or polling stops just as a failed summary is retried.
const SUMMARY_POLL_MAX_ATTEMPTS = 30;

export const SessionResults: React.FC = () => {
  const { assessmentId } = useParams<{ assessmentId: string }>();
  const navigate = useNavigate();
  const { getToken } = useSessionToken();
  const isGuest = useIsGuest();
  const sessionRoutes = useSessionRoutes();
  const { reset } = useInterviewStore();
  const enterTemplate = useSessionWizardStore((s) => s.enterTemplate);
  const enterAssessment = useEnterAssessment();
  const handleIfRevoked = useRevokedKickOut();
  const { notification } = App.useApp();

  const [assessment, setAssessment] = useState<Assessment | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [retakeModalOpen, setRetakeModalOpen] = useState(false);
  const [retrying, setRetrying] = useState(false);
  const [retryError, setRetryError] = useState<string | null>(null);
  // "Try Again" has to ask the server whether this is a resume before it can
  // decide between the picker and going straight in — hence a loading state
  // on a button that used to just open a modal.
  const [checkingForOpenSitting, setCheckingForOpenSitting] = useState(false);

  // The HTS questionnaire still open on this template, if any — offered once
  // via NextPartModal so finishing here doesn't strand the other part.
  const [nextPart, setNextPart] = useState<TemplatePart | null>(null);

  // The assessment a summary poll already gave up on (see below).
  const [summaryPollGaveUpFor, setSummaryPollGaveUpFor] = useState<string | null>(null);

  useEffect(() => {
    if (!assessmentId) return;
    let cancelled = false;
    const load = async () => {
      try {
        const token = await getToken();
        if (!token) return;
        const data = await new AssessmentService(token).getAssessment(assessmentId);
        if (!cancelled) setAssessment(data);
      } catch (err) {
        if (!cancelled) setError(BaseAPIClient.parseErrorMsg(err));
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [assessmentId, getToken]);

  useEffect(() => {
    if (assessment?.resultsPending) navigate(sessionRoutes.hub, { replace: true });
  }, [assessment, navigate, sessionRoutes]);

  // The summary is generated in the background as the sitting completes, so
  // a candidate arriving straight from Submit usually gets here first — poll
  // until it lands. Gives up after SUMMARY_POLL_MAX_ATTEMPTS (its generation
  // most likely failed) rather than showing a spinner forever.
  const awaitingSummary =
    assessment !== null &&
    assessment.status === AssessmentStatus.Completed &&
    !assessment.resultsPending &&
    assessment.summaryNarrative === null;
  useEffect(() => {
    if (!awaitingSummary || !assessmentId) return;
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout>;
    let attempts = 0;
    const poll = async () => {
      attempts++;
      try {
        const token = await getToken();
        if (token) {
          const data = await new AssessmentService(token).getAssessment(assessmentId);
          if (cancelled) return;
          if (data.summaryNarrative !== null) {
            setAssessment(data);
            return;
          }
        }
      } catch (err) {
        console.error('Polling for session summary failed:', BaseAPIClient.parseErrorMsg(err));
      }
      if (cancelled) return;
      if (attempts >= SUMMARY_POLL_MAX_ATTEMPTS) {
        setSummaryPollGaveUpFor(assessmentId);
        return;
      }
      timer = setTimeout(poll, SUMMARY_POLL_INTERVAL_MS);
    };
    timer = setTimeout(poll, SUMMARY_POLL_INTERVAL_MS);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [awaitingSummary, assessmentId, getToken]);
  const summaryLoading = awaitingSummary && summaryPollGaveUpFor !== assessmentId;

  // Land back on this interview's "Where to start" step rather than an empty
  // card, so the other questionnaire is right there. (A load failure has no
  // template to return to; the card keeps whatever it had.)
  const handleBack = () => {
    reset();
    if (assessment) enterTemplate(assessment.interviewTemplate, null);
    navigate(sessionRoutes.hub);
  };

  // Fetched separately rather than read off `assessment.interviewTemplate`:
  // the HTS status fields are only computed by the templates list endpoint.
  // Keyed on plain values, not `assessment` itself, which the summary effect
  // above replaces moments after load. The ref keeps it to one prompt per
  // visit even when storage is unavailable and markPrompted can't persist.
  const promptAssessmentId = assessment?.status === AssessmentStatus.Completed ? assessment.id : null;
  const promptTemplateId = assessment?.interviewTemplateId ?? null;
  const promptHtsRequired = assessment?.interviewTemplate.htsRequired ?? false;
  const promptHandledRef = useRef(false);
  useEffect(() => {
    if (isGuest || !promptAssessmentId || !promptTemplateId || !promptHtsRequired) return;
    if (promptHandledRef.current || wasPrompted(promptAssessmentId)) return;
    let cancelled = false;
    (async () => {
      try {
        const token = await getToken();
        if (!token) return;
        const templates = await new InterviewTemplatesService(token).list();
        const template = templates.find((t) => t.id === promptTemplateId);
        if (cancelled || !template || promptHandledRef.current) return;
        setNextPart(remainingPart(partsOf(template, new Map()), 'comp'));
      } catch (err) {
        // Only the optional prompt depends on this — the page itself is fine.
        console.error('Next-part lookup failed:', BaseAPIClient.parseErrorMsg(err));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [promptAssessmentId, promptTemplateId, promptHtsRequired, isGuest, getToken]);

  const closeNextPart = () => {
    promptHandledRef.current = true;
    if (assessment) markPrompted(assessment.id);
    setNextPart(null);
  };

  const startNextPart = () => {
    if (!assessment || !nextPart) return;
    closeNextPart();
    reset();
    enterTemplate(assessment.interviewTemplate, nextPart.key);
    navigate(sessionRoutes.hub);
  };

  const startHts = () => {
    if (!assessment) return;
    reset();
    enterTemplate(assessment.interviewTemplate, 'hts');
    navigate(sessionRoutes.hub);
  };

  const nextPartPrompt = nextPart && assessment && (
    <NextPartModal
      title="Competency questionnaire complete"
      body={
        assessment.resultsPending
          ? 'Your results are being reviewed. In the meantime, this interview has one more questionnaire to finish.'
          : 'Your results are ready. This interview has one more questionnaire to finish.'
      }
      finishedLabel="Competency Questionnaire"
      finishedDetail="Completed"
      nextLabel={nextPart.label}
      nextDetail={remainingPartCopy(nextPart).detail}
      nextNotStarted={nextPart.status === 'new'}
      primaryLabel={remainingPartCopy(nextPart).primaryLabel}
      dismissLabel={assessment.resultsPending ? 'Not now' : 'View results'}
      onPrimary={startNextPart}
      onDismiss={closeNextPart}
    />
  );
  usePageBackLink(handleBack, 'Back to Sessions');

  // Guests get no back link in MainLayout's header (it shows the wordmark
  // instead), so this is their only way off this page that isn't ending the
  // session — hence unconditional, unlike the retake button below. It always
  // goes to the hub: that's where either flow actually starts, and the guest's
  // session is still live, so there's nothing to re-establish via /redeem.
  // Labelled for the questionnaire still outstanding on this template when
  // there is one, since that's the step the hub will land them on.
  usePageHeaderAction(
    isGuest ? (
      <BrandButton appearance="solid" size="small" onClick={() => navigate(sessionRoutes.hub)}>
        {assessment?.interviewTemplate.htsRequired && !assessment.interviewTemplate.htsEverSubmitted
          ? 'HTS Questionnaire'
          : 'Back to Sessions'}
      </BrandButton>
    ) : null
  );

  const heroFacts = useMemo(() => {
    if (!assessment) return [];
    const questionCount = assessment.responses.length;
    const sittingDate = assessment.completedAt ?? assessment.createdAt;
    // The trend window only holds the latest MAX_ATTEMPT_HISTORY sittings while
    // attemptNumber keeps counting through the full history, so its length would
    // pin this badge at "Attempt 10" from attempt 11 on. Read the number off
    // *this* sitting's entry — the page can be opened on an older attempt too,
    // which the last entry wouldn't describe either.
    const trend = assessment.scoreTrend?.overall ?? [];
    const attemptNumber =
      trend.find((attempt) => attempt.assessmentId === assessment.id)?.attemptNumber ??
      trend.at(-1)?.attemptNumber ??
      1;
    const spoken = assessment.responses.some((r) => r.inputMode === AssessmentInputMode.VOICE);

    return [
      { label: `${questionCount} ${pluralize(questionCount, 'question', 'questions')}`, icon: <ListChecks aria-hidden="true" /> },
      { label: formatDate(sittingDate), icon: <Calendar aria-hidden="true" /> },
      ...(attemptNumber > 1 ? [{ label: `Attempt ${attemptNumber}`, icon: <RotateCw aria-hidden="true" /> }] : []),
      ...(spoken ? [{ label: 'Voice answers', icon: <Mic aria-hidden="true" /> }] : []),
    ];
  }, [assessment]);

  // Seeds the answer box with what was said last time. Keyed off *this*
  // results page's sitting, not whichever one we end up entering — it's the
  // attempt being retried.
  const priorAnswersByItemId = useMemo(() => {
    const map = new Map<string, string>();
    for (const response of assessment?.responses ?? []) {
      if (response.interviewTemplateItemId) {
        map.set(response.interviewTemplateItemId, response.answer);
      }
    }
    return map;
  }, [assessment]);

  /**
   * Two outcomes, decided by whether a sitting for this template is already
   * open. If one is, `createAssessment` would hand that same sitting back and
   * ignore the selection entirely — it only re-scopes a sitting with no
   * answers yet — so showing the question picker would be a lie. Resume it
   * instead, exactly as Sessions' "Continue" does. Otherwise this really is a
   * fresh sitting and the picker decides its scope.
   */
  const handleTryAgain = async () => {
    if (!assessment || checkingForOpenSitting) return;
    setRetryError(null);
    setCheckingForOpenSitting(true);
    try {
      const token = await getToken();
      if (!token) throw new Error('Not authenticated');
      const service = new AssessmentService(token);

      const openSittingId = await service.findActiveAssessmentId(assessment.interviewTemplateId);
      if (!openSittingId) {
        setRetakeModalOpen(true);
        return;
      }

      const openSitting = await service.getAssessment(openSittingId);
      await enterAssessment(openSitting, { priorAnswersByItemId });
    } catch (err) {
      if (handleIfRevoked(err)) return;
      // No modal is open on this path, so its inline Alert can't carry the error.
      notification.error({ message: BaseAPIClient.parseErrorMsg(err) });
    } finally {
      setCheckingForOpenSitting(false);
    }
  };

  const handleConfirmRetake = async (selectedItemIds: string[], differentItemIds: string[]) => {
    if (!assessment) return;
    setRetryError(null);
    setRetrying(true);
    try {
      const token = await getToken();
      if (!token) throw new Error('Not authenticated');

      // Same endpoint/charge path as any new sitting — a retry is just
      // another Assessment on the same template. handleTryAgain has already
      // established nothing was open for this template, but the server is
      // still the authority: another tab could have opened one since, in which
      // case this resumes rather than creating, and enterAssessment handles it.
      const newAssessment = await new AssessmentService(token).createAssessment(
        assessment.interviewTemplateId,
        selectedItemIds,
        differentItemIds,
        assessment.id
      );

      if (newAssessment.substitutionDeadEnds && newAssessment.substitutionDeadEnds.length > 0) {
        notification.info({
          message: 'Question pool exhausted',
          description:
            "There's no other question of that competency and type left in the bank yet, so we kept the one you last answered for it.",
          classNames: { root: styles.substitutionNotice },
        });
      }

      await enterAssessment(newAssessment, { priorAnswersByItemId });
    } catch (err) {
      if (handleIfRevoked(err)) return;
      setRetryError(BaseAPIClient.parseErrorMsg(err));
      setRetrying(false);
    }
  };

  if (loading) {
    return (
      <div className={styles.page}>
        <div className={styles.centerSpin}>
          <Spin size="large" />
        </div>
      </div>
    );
  }

  if (error || !assessment) {
    return (
      <div className={styles.page}>
        <Alert type="error" message={error ?? 'Session not found'} showIcon />
      </div>
    );
  }

  if (assessment.resultsPending) {
    return (
      <div className={styles.page}>
        <div className={styles.centerSpin}>
          <Spin size="large" />
        </div>
      </div>
    );
  }

  return (
    <div className={styles.page}>
      <button type="button" className={styles.backLink} onClick={handleBack}>
        <ArrowLeft size={16} />
        Back to Sessions
      </button>

      <div className={styles.body}>
        <motion.div
          className={styles.hero}
          initial={{ y: -16, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.4, ease: 'easeOut' }}
        >
          <p className={styles.heroEyebrow}>Results</p>
          <Title level={1} className={styles.heroTitle}>
            {assessment.interviewTemplate.name}
          </Title>
          <ul className={styles.heroMeta}>
            {heroFacts.map((fact) => (
              <li key={fact.label}>
                {fact.icon}
                {fact.label}
              </li>
            ))}
          </ul>
          {/* Guests get one attempt per template (see guestAttemptGuard.ts on the
              backend) — a retry here would just 403, so it's hidden rather than
              shown-then-rejected. A revoked package leaves nothing to start
              either — its results stay readable, but the CTAs go. */}
          {!isGuest && !assessment.interviewTemplate.revokedAt && (
            <div className={styles.heroActions}>
              {assessment.interviewTemplate.htsRequired && !assessment.interviewTemplate.htsEverSubmitted && (
                <BrandButton appearance="outline" onClick={startHts}>
                  Start HTS
                </BrandButton>
              )}
              <BrandButton appearance="solid" loading={checkingForOpenSitting} onClick={handleTryAgain}>
                Try Again
              </BrandButton>
            </div>
          )}
        </motion.div>

        <div className={styles.chartSection}>
          <EvalChart
            responses={assessment.responses}
            totalScore={assessment.totalScore}
            totalScoreDelta={assessment.totalScoreDelta}
            summaryNarrative={
              assessment.status === AssessmentStatus.Completed ? assessment.summaryNarrative : null
            }
            summaryLoading={summaryLoading}
            scoreTrend={assessment.scoreTrend}
          />
        </div>
      </div>

      {nextPartPrompt}

      {retakeModalOpen && (
        <RetakeModal
          assessment={assessment}
          confirming={retrying}
          error={retryError}
          onCancel={() => setRetakeModalOpen(false)}
          onConfirm={handleConfirmRetake}
        />
      )}
    </div>
  );
};
